import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
// ngrx
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
// rxjs
import { combineLatest, of } from "rxjs";
import {
    catchError,
    exhaustMap,
    map,
    switchMap,
    withLatestFrom,
} from "rxjs/operators";
// Store
import * as fromRoot from "#core/router-store";
import * as studentsActions from "../actions/students.action";
import * as coursesActions from "../actions/teacher-classes.action";
// Services
import { UpdateFidoProfileHelper } from "#core/helpers/update-fido-profile.helper";
import { SnackBarService } from "#services/snackbar.service";
import { USER_SERVICE } from "@carnegie-learning/data-access-angular-fido";
import { AssignmentsService, CoursesService, FidoStudentCreateRequest, ProfileFidoService, StudentsService } from "@carnegie-learning/data-access-angular-passport";
import { UserService } from "@carnegie-learning/data-access-fido";
import { Student } from "cli-types";

@Injectable()
export class StudentsEffects {
    working = false;
    constructor(
        private actions$: Actions,
        private studentsService: StudentsService,
        private coursesService: CoursesService,
        private assignmentsService: AssignmentsService,
        private store: Store<fromRoot.CustomSerializer>,
        private snackBarService: SnackBarService,
        private router: Router,
        @Inject(USER_SERVICE) private userService: UserService,
        private profileFidoService: ProfileFidoService,
        private updateFidoProfileHelper: UpdateFidoProfileHelper,
    ) {}

    loadStudents$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.loadStudents)).pipe(
            // gets router params (in this case the courseID)
            // and sends it with service call
            map((action) => action),
            switchMap((latestPayload) => {
                return this.coursesService
                    .getStudents(latestPayload.payload, latestPayload.courseID)
                    .pipe(
                        map((students) => students),
                        map((response) =>
                            studentsActions.loadStudentsSuccess({
                                payload: response,
                            })
                        ),
                        catchError((error) =>
                            of(
                                studentsActions.loadStudentsFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );

    loadStudent$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.loadStudent)).pipe(
            map((action) => action.studentID),
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    if (router) {
                        return {
                            studentID: action,
                            courseID: router.state.params.courseID,
                        };
                    } else {
                        return {
                            studentID: action,
                            courseID: null,
                        };
                    }
                }
            ),
            switchMap((latestPayload) => {
                return this.coursesService
                    .getStudent(latestPayload.courseID, latestPayload.studentID)
                    .pipe(
                        map((student) => student),
                        map((student) =>
                            studentsActions.loadStudentSuccess({
                                payload: student,
                            })
                        ),
                        catchError((error) =>
                            of(
                                studentsActions.loadStudentFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );
    loadStudentMetrics$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.loadStudentMetrics)).pipe(
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    if (router) {
                        return {
                            courseID: router.state.params.courseID,
                            studentID: router.state.params.userID,
                        };
                    } else {
                        return {
                            courseID: null,
                            studentID: null,
                        };
                    }
                }
            ),
            switchMap((latestPayload) => {
                return this.assignmentsService
                    .getStudentMetrics(
                        latestPayload.courseID,
                        latestPayload.studentID
                    )
                    .pipe(
                        map((studentMetrics) =>
                            studentsActions.loadStudentMetricsSuccess({
                                payload: studentMetrics,
                            })
                        ),
                        catchError((error) =>
                            of(
                                studentsActions.loadStudentMetricsFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );
    createFidoStudent$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.createFidoStudent)).pipe(
            switchMap((action) => {
                // Map student form data to a request Object
                const formattedData: FidoStudentCreateRequest = {
                    user: {
                        loginId: action.payload.eAddress,
                        givenName: action.payload.firstName,
                        surName: action.payload.lastName,
                        email: action.payload.eAddress,
                        role: this.updateFidoProfileHelper.convertRole(action.payload.roleLevel),
                        altId: "",
                        externalId: "",
                        fullName: "",
                        tenantId: ""
                    },
                    authorization: {
                        school: action.schoolID,
                        role: "student",
                    },
                };

                // Create a new student FIDO user then pass the baton to Passport with the FIDO id and teacher-class
                return this.profileFidoService
                    .createFidoStudent(formattedData)
                    .pipe(
                        map((addedStudent) => {
                            return studentsActions.setStudentFidoPassword({
                                studentUUIDs: [addedStudent.id],
                                courseID: action.courseID,
                                password: action.payload.tempPassword,
                                syncAssignments: (<any>action.payload)
                                    .sync_assignments,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.createFidoStudentFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );
    updateFidoStudent$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.updateFidoStudent)).pipe(
            switchMap((action) => {
                // update a FIDO user
                return this.userService
                    .updateUser({ ...action.user, id: action.fidoUserID })
                    .pipe(
                        map((resp) => {
                            return studentsActions.updateFidoStudentSuccess({
                                payload: resp,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.updateFidoStudentFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );

    setFidoStudentPassword$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.setStudentFidoPassword)).pipe(
            switchMap((action) => {
                // Create a new student FIDO user then pass the baton to Passport with the FIDO id and teacher-class
                return this.profileFidoService
                    .updateFidoStudentPassword(
                        action.studentUUIDs[0],
                        <string>action.password
                    )
                    .pipe(
                        map((addedStudent) => {
                            return studentsActions.addStudents({
                                studentIDList: action.studentUUIDs,
                                courseID: action.courseID,
                                syncAssignments: action.syncAssignments,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.setStudentFidoPasswordFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );
    updateFidoStudentPassword$ = createEffect(() =>
        this.actions$
            .pipe(ofType(studentsActions.updateStudentFidoPassword))
            .pipe(
                switchMap((action) => {
                    return this.profileFidoService
                        .updateFidoStudentPassword(
                            action.fidoUserID,
                            <string>action.password
                        )
                        .pipe(
                            map((resp) => {
                                // PAS-9119 and PAS-9216 Student updated message only should be displayed
                                // if (resp) {
                                //     this.snackBarService.success(
                                //         "Password Updated"
                                //     );
                                // }
                                return studentsActions.updateStudentFidoPasswordSuccess(
                                    {
                                        payload: resp,
                                    }
                                );
                            }),
                            catchError((error) =>
                                of(
                                    studentsActions.updateStudentFidoPasswordFail(
                                        { payload: error }
                                    )
                                )
                            )
                        );
                })
            )
    );
    addStudents$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.addStudents)).pipe(
            switchMap((action) => {
                this.working = true;
                if (this.working && action.syncAssignments) {
                    this.snackBarService.working("Working...");
                }
                // Create a new student FIDO user then pass the baton to Passport with the FIDO id and teacher-class
                return this.coursesService
                    .addSelectedStudents(
                        { selectedStudents: action.studentIDList },
                        action.courseID,
                        action.syncAssignments
                    )
                    .pipe(
                        map((studentList) => {
                            this.working = false;
                            if (action.studentIDList.length > 1) {
                                this.snackBarService.success("Students Added!");
                            } else {
                                this.snackBarService.success("Student Added!");
                            }

                            const school = action.school ?? "";
                            return studentsActions.addStudentsSuccess({
                                payload: studentList,
                                school: school,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.addStudentsFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );

    syncSchoolPermission$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.addStudentsSuccess)).pipe(
            exhaustMap((action) => {
                const studentIDs = (<Student[]>action.payload).map(
                    (student) => student.userGUID
                );
                return combineLatest([
                    ...studentIDs.map((id) => {
                        // only make this request if the school data exists
                        if (action.school && action.school.length > 0) {
                            return this.profileFidoService.updateFidoPermissions(
                                id,
                                {
                                    school: action.school,
                                    role: "student",
                                }
                            );
                        }
                    }),
                ]).pipe(
                    map((data) => {
                        return studentsActions.updateSchoolPermissionSuccess();
                    }),
                    catchError((error) =>
                        of(
                            studentsActions.updateSchoolPermissionFail({
                                payload: error,
                            })
                        )
                    )
                );
            })
        )
    );

    updateStudent$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.updateStudent)).pipe(
            map((action) => action),
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    if (router) {
                        return {
                            studentForm: action.payload,
                            courseID: router.state.params.courseID,
                            userID: router.state.params.userID,
                        };
                    } else {
                        return {
                            studentForm: action.payload,
                            courseID: null,
                            userID: null,
                        };
                    }
                }
            ),
            switchMap((latestPayload) => {
                return this.coursesService
                    .updateStudent(
                        latestPayload.studentForm,
                        latestPayload.courseID,
                        latestPayload.userID
                    )
                    .pipe(
                        map((updatedStudent) => {
                            // PAS-9119 update fido student action is showing User updated or password updated, double snackBar confused user.
                            this.snackBarService.success("Student Updated!");
                            return studentsActions.updateStudentSuccess({
                                payload: updatedStudent,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.updateStudentFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );
    handleStudentCreateSuccess$ = createEffect(() =>
        // Routing effect for successful create
        this.actions$
            .pipe(
                ofType(
                    studentsActions.addStudentSuccess,
                    studentsActions.addStudentsSuccess,
                    studentsActions.updateStudentSuccess
                )
            )
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        if (router && router.state.params.districtID) {
                            return {
                                districtID: router.state.params.districtID,
                                schoolID: router.state.params.schoolID,
                                teacherID: router.state.params.teacherID,
                                courseID: router.state.params.courseID,
                            };
                        } else {
                            return {
                                districtID: null,
                                schoolID: null,
                                teacherID: null,
                                courseID: router.state.params.courseID,
                            };
                        }
                    }
                ),
                map((routerPayload) => {
                    if (routerPayload.districtID) {
                        return new fromRoot.Go({
                            path: [
                                `distributor/districts/${routerPayload.districtID}/schools/${routerPayload.schoolID}/teachers/${routerPayload.teacherID}/classes/${routerPayload.courseID}`,
                            ],
                        });
                    } else {
                        return new fromRoot.Go({
                            path: [`teacher/classes/${routerPayload.courseID}/students`],
                        });
                    }
                })
            )
    );
    archiveStudent$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.archiveStudent)).pipe(
            map((action) => action.payload),
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    if (router) {
                        return {
                            student: action,
                            courseID: router.state.params.courseID,
                        };
                    } else {
                        return {
                            student: action,
                            courseID: null,
                        };
                    }
                }
            ),
            switchMap((latestPayload) => {
                return this.coursesService
                    .archiveStudent(
                        latestPayload.student,
                        latestPayload.courseID
                    )
                    .pipe(
                        map((archivedStudent) => {
                            this.snackBarService.success("Student Archived!");
                            return studentsActions.archiveStudentSuccess({
                                payload: archivedStudent,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.archiveStudentFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );
    archiveStudentSuccess$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(
                    studentsActions.addStudentSuccess,
                    studentsActions.addStudentsSuccess,
                    studentsActions.archiveStudentSuccess,
                    studentsActions.reactivateStudentSuccess
                )
            )
            .pipe(
                map((action) => action.payload),
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        if (router) {
                            return {
                                student: action,
                                courseID: router.state.params.courseID,
                            };
                        } else {
                            return {
                                student: action,
                                courseID: null,
                            };
                        }
                    }
                ),
                switchMap((latestPayload) => {
                    return [
                        coursesActions.loadCourseMetrics({
                            courseID: parseInt(latestPayload.courseID, 10),
                        }),
                        coursesActions.loadCoursesDropdown(),
                    ];
                })
            )
    );

    reactivateStudent$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.reactivateStudent)).pipe(
            map((action) => action.payload),
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    if (router) {
                        return {
                            student: action,
                            courseID: router.state.params.courseID,
                        };
                    } else {
                        return {
                            student: action,
                            courseID: null,
                        };
                    }
                }
            ),
            switchMap((latestPayload) => {
                return this.coursesService
                    .reactivateStudent(
                        latestPayload.student,
                        latestPayload.courseID
                    )
                    .pipe(
                        map((reactivatedStudent) => {
                            this.snackBarService.success(
                                "Student Reactivated!"
                            );
                            return studentsActions.reactivateStudentSuccess({
                                payload: reactivatedStudent,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.reactivateStudentFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );
    removeStudent$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.removeStudent)).pipe(
            map((action) => action.payload),
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    if (router) {
                        return {
                            student: action,
                            courseID: router.state.params.courseID,
                        };
                    } else {
                        return {
                            student: action,
                            courseID: null,
                        };
                    }
                }
            ),
            switchMap((latestPayload) => {
                return this.coursesService
                    .removeStudent(
                        latestPayload.student,
                        latestPayload.courseID
                    )
                    .pipe(
                        map((removedStudent) => {
                            this.snackBarService.success(
                                "Student Removed From Class!"
                            );
                            return studentsActions.removeStudentSuccess({
                                payload: latestPayload.student,
                            });
                        }),
                        catchError((error) =>
                            of(
                                studentsActions.removeStudentFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );

    loadStudentsFidoData$ = createEffect(() =>
        this.actions$.pipe(ofType(studentsActions.loadStudentsSuccess)).pipe(
            switchMap((payload) => {
                const uuIDs: string[] = [];
                payload.payload.data.forEach((profileData) => {
                    if (profileData.firstName === null) {
                        // only grab data for data that is missing from FIDO
                        uuIDs.push(profileData.userGUID);
                    }
                });
                return this.userService.getUsers(uuIDs).pipe(
                    map((fidoUserData) =>
                        studentsActions.loadStudentsFidoDataSuccess({
                            fidoData: fidoUserData,
                        })
                    ),
                    catchError((error) =>
                        of(
                            studentsActions.loadStudentsFidoDataFail({
                                payload: error,
                            })
                        )
                    )
                );
            })
        )
    );
}
